import { useCallback, useMemo, type KeyboardEvent } from 'react'
import { groupBy } from 'es-toolkit'
import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { StandardApplicability } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'
import { buildTypeBCandidateExplanation } from '../utils/buildTypeBCandidateExplanation'

const parseStandardTitle = (title: string) => {
  const parts = title.split(' - ')
  const category1 = parts[0]?.trim() ?? ''
  const category2 = parts[1]?.trim() ?? ''
  const specific = parts.slice(2).join(' - ').trim()
  return {
    category1,
    category2,
    specific,
  }
}

function getStandardCategoryKey(standard: StandardApplicability): string {
  const { category1 } = parseStandardTitle(standard.title)
  return category1.length > 0 ? category1 : '—'
}

function getGroupSelectionState(
  groupStandards: StandardApplicability[],
  selectedIds: Set<string>,
  lockedIds: Set<string>
): {
  checked: boolean
  indeterminate: boolean
  disabled: boolean
} {
  const toggleableStandards = groupStandards.filter(
    (standard) => !lockedIds.has(standard.id)
  )
  if (toggleableStandards.length === 0) {
    return { checked: false, indeterminate: false, disabled: true }
  }
  const selectedCount = toggleableStandards.filter((standard) =>
    selectedIds.has(standard.id)
  ).length
  if (selectedCount === 0) {
    return { checked: false, indeterminate: false, disabled: false }
  }
  if (selectedCount === toggleableStandards.length) {
    return { checked: true, indeterminate: false, disabled: false }
  }
  return { checked: false, indeterminate: true, disabled: false }
}

function OnboardingStandardsSelectionRow({
  standard,
  isSelected,
  isLocked,
  isDisabled,
  onCheckedChange,
}: {
  standard: StandardApplicability
  isSelected: boolean
  isLocked: boolean
  isDisabled: boolean
  onCheckedChange: (standardId: string, checked: boolean) => void
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const handleCheckedChange = useCallback(
    (checked: boolean) => {
      onCheckedChange(standard.id, checked === true)
    },
    [onCheckedChange, standard.id]
  )

  const ariaLabel = useMemo(() => {
    return t('onboarding.typeCStandardsReviewTable.keepStandardAria', {
      reference: standard.reference,
    })
  }, [t, standard.reference])

  const titleParts = parseStandardTitle(standard.title)
  const explanationText = useMemo(() => {
    if (standard.source === 'QUESTIONNAIRE_RULES') {
      const localizedExplanation = buildTypeBCandidateExplanation(
        standard.reference,
        t,
        standard.explanation
      )
      if (localizedExplanation.length > 0) {
        return localizedExplanation
      }
    }
    return standard.explanation?.trim() ?? ''
  }, [standard.explanation, standard.reference, standard.source, t])
  const subtitleParts = [titleParts.category2, titleParts.specific].filter(
    (part) => part.length > 0
  )
  const isRowDisabled = isDisabled || isLocked

  const handleRowClick = useCallback(() => {
    if (isRowDisabled) {
      return
    }
    handleCheckedChange(!isSelected)
  }, [handleCheckedChange, isRowDisabled, isSelected])

  const handleRowKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (isRowDisabled) {
        return
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        handleCheckedChange(!isSelected)
      }
    },
    [handleCheckedChange, isRowDisabled, isSelected]
  )

  return (
    <div
      role='button'
      tabIndex={isRowDisabled ? -1 : 0}
      onClick={handleRowClick}
      onKeyDown={handleRowKeyDown}
      className={cn(
        'flex items-start gap-3 rounded-md border border-transparent px-2 py-2 hover:border-border',
        isRowDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
      )}
    >
      <Checkbox
        checked={isSelected}
        disabled={isRowDisabled}
        onCheckedChange={handleCheckedChange}
        onClick={(event) => {
          event.stopPropagation()
        }}
        aria-label={ariaLabel}
        className='mt-0.5'
      />
      <div className='min-w-0 flex-1 space-y-0.5'>
        <div className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5'>
          <p className='font-medium'>{standard.reference}</p>
          {explanationText ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  className='text-xs text-primary underline-offset-4 hover:underline'
                  aria-label={t(
                    'onboarding.typeCStandardsReviewTable.expandDetailsAria',
                    { reference: standard.reference }
                  )}
                  onClick={(event) => {
                    event.stopPropagation()
                  }}
                >
                  {t('onboarding.typeCStandardsReviewTable.whyLink')}
                </button>
              </TooltipTrigger>
              <TooltipContent side='top' className='max-w-md text-left'>
                {explanationText}
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
        {subtitleParts.length > 0 ? (
          <p className='text-sm text-muted-foreground'>
            {subtitleParts.join(' - ')}
          </p>
        ) : null}
      </div>
    </div>
  )
}

function OnboardingStandardsSelectionCategoryCard({
  category,
  standards,
  selectedIds,
  lockedIds,
  disabled,
  onToggle,
}: {
  category: string
  standards: StandardApplicability[]
  selectedIds: Set<string>
  lockedIds: Set<string>
  disabled: boolean
  onToggle: (standardId: string, checked: boolean) => void
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const sortedStandards = useMemo(() => {
    return [...standards].sort((first, second) =>
      first.reference.localeCompare(second.reference)
    )
  }, [standards])
  const groupSelection = useMemo(() => {
    return getGroupSelectionState(sortedStandards, selectedIds, lockedIds)
  }, [sortedStandards, selectedIds, lockedIds])
  const selectedInGroup = useMemo(() => {
    return sortedStandards.filter((standard) => selectedIds.has(standard.id)).length
  }, [sortedStandards, selectedIds])

  const handleToggleGroup = useCallback(
    (checked: boolean) => {
      for (const standard of sortedStandards) {
        if (lockedIds.has(standard.id)) {
          continue
        }
        onToggle(standard.id, checked)
      }
    },
    [lockedIds, onToggle, sortedStandards]
  )

  return (
    <Card className='overflow-hidden py-0'>
      <Collapsible className='group/category'>
        <div className='flex items-start gap-3 px-4 py-3'>
          <Checkbox
            checked={groupSelection.checked}
            indeterminate={groupSelection.indeterminate}
            disabled={disabled || groupSelection.disabled}
            onCheckedChange={handleToggleGroup}
            aria-label={t('onboarding.typeCStandardsReviewTable.selectCategoryAria', {
              category,
            })}
            className='mt-1'
            onClick={(event) => {
              event.stopPropagation()
            }}
          />
          <CollapsibleTrigger className='flex min-w-0 flex-1 items-start gap-2 text-start'>
            <ChevronRight
              className={cn(
                'mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform duration-200',
                'group-data-[state=open]/category:rotate-90 rtl:rotate-180'
              )}
            />
            <div className='min-w-0 flex-1'>
              <p className='font-semibold leading-snug'>{category}</p>
              <p className='text-sm text-muted-foreground'>
                {t('onboarding.typeCStandardsReviewTable.categorySelectedCount', {
                  selected: selectedInGroup,
                  total: sortedStandards.length,
                })}
              </p>
            </div>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <div className='space-y-1 border-t px-2 py-2'>
            {sortedStandards.map((standard) => {
              return (
                <OnboardingStandardsSelectionRow
                  key={standard.id}
                  standard={standard}
                  onCheckedChange={onToggle}
                  isSelected={selectedIds.has(standard.id)}
                  isLocked={lockedIds.has(standard.id)}
                  isDisabled={disabled}
                />
              )
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}

export function OnboardingStandardsSelectionTable({
  standards,
  selectedIds,
  lockedIds = new Set<string>(),
  disabled = false,
  showSelectAll = true,
  onToggle,
  onToggleAll,
}: {
  standards: StandardApplicability[]
  selectedIds: Set<string>
  lockedIds?: Set<string>
  disabled?: boolean
  showSelectAll?: boolean
  onToggle: (standardId: string, checked: boolean) => void
  onToggleAll?: (checked: boolean) => void
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')

  const allSelected = useMemo(() => {
    if (standards.length === 0) {
      return false
    }
    return standards
      .filter((standard) => !lockedIds.has(standard.id))
      .every((standard) => selectedIds.has(standard.id))
  }, [standards, selectedIds, lockedIds])

  const groupedCategories = useMemo(() => {
    const grouped = groupBy(standards, getStandardCategoryKey)
    return Object.entries(grouped).sort(([firstCategory], [secondCategory]) =>
      firstCategory.localeCompare(secondCategory)
    )
  }, [standards])

  return (
    <div className='flex min-h-0 flex-col gap-4'>
      {showSelectAll && onToggleAll != null ? (
        <div className='flex items-center gap-2 px-1'>
          <Checkbox
            checked={allSelected}
            onCheckedChange={(checked) => {
              onToggleAll(checked === true)
            }}
            aria-label={t('onboarding.typeCStandardsReviewTable.selectAllAria')}
            disabled={disabled || standards.length === 0}
          />
          <span className='text-sm text-muted-foreground'>
            {t('onboarding.typeCStandardsReviewTable.selectAllAria')}
          </span>
        </div>
      ) : null}
      <div className='max-h-[min(60vh,calc(100dvh-16rem))] space-y-3 overflow-auto pe-1'>
        {groupedCategories.map(([category, categoryStandards]) => {
          return (
            <OnboardingStandardsSelectionCategoryCard
              key={category}
              category={category}
              standards={categoryStandards}
              selectedIds={selectedIds}
              lockedIds={lockedIds}
              disabled={disabled}
              onToggle={onToggle}
            />
          )
        })}
      </div>
    </div>
  )
}
